import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAppointments } from "../../../redux/Thunk/Appointment/AppointmentThunks";
import { setSelectedDate } from "../../../redux/Reducer/Appointment/AppointmentSlice";
import { formatDateString } from "../../../utils/helpers";

const CalendarIcon = () => (
  <svg className="w-5 h-5 text-gray-800 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-3.5 h-3.5 text-gray-400 shrink-0 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MonthlyCalendarView = ({ onSelectDate, onSelectAppointment, onNewAppointmentForDate }) => {
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.appointment.selectedDate);
  const appointments = useSelector((state) => state.appointment.appointments);

  // Current viewed month/year state
  const initialDate = selectedDate ? new Date(selectedDate) : new Date();
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth()); // 0-indexed
  const [dayDetailsModal, setDayDetailsModal] = useState(null); // for "+ N more" popup

  // Calculate start and end date for fetching monthly appointments
  useEffect(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    const startDate = formatDateString(firstDayOfMonth);
    const endDate = formatDateString(lastDayOfMonth);

    dispatch(fetchAppointments({ startDate, endDate }));
  }, [dispatch, currentYear, currentMonth]);

  // Navigate Months
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    const todayStr = today.toISOString().split("T")[0];
    dispatch(setSelectedDate(todayStr));
  };

  // Build Calendar Days Matrix
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay(); // 0 = Sun
  const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const prevMonthTotalDays = new Date(currentYear, currentMonth, 0).getDate();

  const calendarCells = [];

  // 1. Previous Month Padding Days
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    const dayNum = prevMonthTotalDays - i;
    const prevMonthDate = new Date(currentYear, currentMonth - 1, dayNum);
    const dateStr = prevMonthDate.toISOString().split("T")[0];
    calendarCells.push({
      dateStr,
      dayNum,
      isCurrentMonth: false,
    });
  }

  // 2. Current Month Days
  for (let dayNum = 1; dayNum <= totalDaysInMonth; dayNum++) {
    const mStr = String(currentMonth + 1).padStart(2, "0");
    const dStr = String(dayNum).padStart(2, "0");
    const dateStr = `${currentYear}-${mStr}-${dStr}`;
    calendarCells.push({
      dateStr,
      dayNum,
      isCurrentMonth: true,
    });
  }

  // 3. Next Month Padding Days (to fill out grid to multiples of 7)
  const remainingCells = 42 - calendarCells.length; // 6 rows * 7 cols = 42
  for (let dayNum = 1; dayNum <= remainingCells; dayNum++) {
    const nextMonthDate = new Date(currentYear, currentMonth + 1, dayNum);
    const dateStr = nextMonthDate.toISOString().split("T")[0];
    calendarCells.push({
      dateStr,
      dayNum,
      isCurrentMonth: false,
    });
  }

  const normalizeDate = (d) => {
    if (!d) return "";
    const str = String(d).trim();
    if (str.includes("T")) return str.split("T")[0];
    if (str.includes(" ")) return str.split(" ")[0];
    return str;
  };

  // Group appointments by date
  const appointmentsByDate = {};
  appointments.forEach((app) => {
    if (!app.appointmentDate) return;
    const d = normalizeDate(app.appointmentDate);
    if (!appointmentsByDate[d]) appointmentsByDate[d] = [];
    appointmentsByDate[d].push(app);
  });

  const todayStr = new Date().toISOString().split("T")[0];

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "CHECKED_IN":
        return "bg-amber-500 text-white";
      case "IN_CONSULTATION":
        return "bg-purple-600 text-white animate-pulse";
      case "COMPLETED":
        return "bg-emerald-600 text-white";
      case "CANCELLED":
        return "bg-red-500 text-white line-through opacity-70";
      case "NO_SHOW":
        return "bg-gray-600 text-white line-through opacity-70";
      default:
        return "bg-blue-600 text-white";
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-6 space-y-6">
      {/* 1. Header & Navigation */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <CalendarIcon />
            <span>Monthly Calendar Overview</span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            View, track, and manage all appointments across the month in visual day blocks
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <button
            type="button"
            onClick={handleToday}
            className="px-3.5 py-1.5 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all shadow-xs cursor-pointer"
          >
            Today
          </button>

          <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-2xl border border-gray-200">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white hover:bg-gray-100 text-gray-700 font-black text-sm shadow-xs transition-all cursor-pointer"
            >
              ‹
            </button>

            <span className="px-3 text-xs font-black text-gray-900 min-w-[120px] text-center">
              {MONTH_NAMES[currentMonth]} {currentYear}
            </span>

            <button
              type="button"
              onClick={handleNextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white hover:bg-gray-100 text-gray-700 font-black text-sm shadow-xs transition-all cursor-pointer"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* 2. Monthly Grid Table */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-xs">
        {/* Days of Week Row */}
        <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200 text-center text-xs font-bold text-gray-700 uppercase tracking-wider py-2.5">
          {DAYS_OF_WEEK.map((day) => (
            <div key={day} className="py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Day Grid Cells (6 rows x 7 cols) */}
        <div className="grid grid-cols-7 divide-x divide-y divide-gray-100 bg-gray-50/30">
          {calendarCells.map((cell, index) => {
            const dayApps = appointmentsByDate[cell.dateStr] || [];
            const isToday = cell.dateStr === todayStr;
            const isSelected = cell.dateStr === selectedDate;

            return (
              <div
                key={`${cell.dateStr}-${index}`}
                onClick={() => {
                  dispatch(setSelectedDate(cell.dateStr));
                  if (onSelectDate) onSelectDate(cell.dateStr);
                }}
                className={`min-h-[110px] p-2 flex flex-col justify-between transition-all cursor-pointer group ${
                  !cell.isCurrentMonth
                    ? "bg-gray-50/60 text-gray-400"
                    : isSelected
                    ? "bg-primary-50/40 border-2 border-primary-500 shadow-xs"
                    : isToday
                    ? "bg-blue-50/30"
                    : "bg-white hover:bg-gray-50/80"
                }`}
              >
                {/* Day Header */}
                <div className="flex items-center justify-between mb-1.5">
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                      isToday
                        ? "bg-primary-600 text-white shadow-xs"
                        : isSelected
                        ? "bg-primary-700 text-white"
                        : cell.isCurrentMonth
                        ? "text-gray-800"
                        : "text-gray-400"
                    }`}
                  >
                    {cell.dayNum}
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setSelectedDate(cell.dateStr));
                        if (onNewAppointmentForDate) onNewAppointmentForDate(cell.dateStr);
                      }}
                      title={`Book Appointment for ${cell.dateStr}`}
                      className="opacity-0 group-hover:opacity-100 transition-all px-2 py-0.5 rounded-lg bg-primary-50 hover:bg-primary-600 text-primary-700 hover:text-white border border-primary-200/80 hover:border-primary-600 text-[10px] font-extrabold flex items-center gap-1 shadow-2xs cursor-pointer active:scale-95"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      <span className="hidden sm:inline font-black">Book</span>
                    </button>

                    {dayApps.length > 0 && (
                      <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                        {dayApps.length}
                      </span>
                    )}
                  </div>
                </div>

                {/* Appointment Tiles List in Day Block - Scrollable */}
                <div className="flex-1 space-y-1 overflow-y-auto max-h-[85px] pr-0.5 custom-scrollbar">
                  {dayApps.map((app) => (
                    <div
                      key={app.AID}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setSelectedDate(cell.dateStr));
                        if (onSelectAppointment) onSelectAppointment(app);
                      }}
                      className={`px-1.5 py-1 rounded-lg text-[10px] font-bold flex items-center justify-between gap-1 shadow-2xs hover:scale-[1.02] transition-all cursor-pointer ${getStatusBadgeStyle(
                        app.status
                      )}`}
                      title={`${app.patientName} (${app.slotStartTime})`}
                    >
                      <span className="truncate max-w-[80px]">
                        #{app.tokenNumber} {app.patientName}
                      </span>
                      <span className="shrink-0 text-[9px] opacity-90">
                        {app.slotStartTime}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day Details Modal for "+ N more" */}
      {dayDetailsModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl p-5 space-y-4 animate-fadeIn border border-gray-100">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 className="font-black text-gray-900 text-sm">
                Appointments on {dayDetailsModal.dateStr} ({dayDetailsModal.apps.length})
              </h3>
              <button
                type="button"
                onClick={() => setDayDetailsModal(null)}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {dayDetailsModal.apps.map((app) => (
                <div
                  key={app.AID}
                  onClick={() => {
                    setDayDetailsModal(null);
                    dispatch(setSelectedDate(dayDetailsModal.dateStr));
                    if (onSelectAppointment) onSelectAppointment(app);
                  }}
                  className="p-3 rounded-2xl border border-gray-100 hover:bg-gray-50 flex items-center justify-between text-xs cursor-pointer transition-all"
                >
                  <div>
                    <span className="font-bold text-gray-900 block">
                      #{app.tokenNumber} {app.patientName}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      <PhoneIcon />
                      {app.patientPhone}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-700 block mb-1">
                      {app.slotStartTime}
                    </span>
                    <span className="text-[10px] font-bold text-primary-600">
                      ₹{app.consultationFee}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyCalendarView;
